/**
 * Protected Route Component
 *
 * Wrapper for routes that require authentication.
 * Optionally checks for specific user roles.
 *
 * Uses React Router's Outlet pattern for nested routes.
 */

import { Navigate, Outlet } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { type UserRole, getUserRole, isClerkConfigured } from '../lib/clerk';

interface ProtectedRouteProps {
  /**
   * Minimum role required to access this route.
   * If not specified, any authenticated user can access.
   */
  requireRole?: UserRole;
  /**
   * If true, redirect to custom URL instead of sign-in
   */
  redirectTo?: string;
}

/**
 * Redirects users to sign-in if not authenticated.
 * Redirects to dashboard if authenticated but lacking required role.
 *
 * Use this component as a wrapper with an <Outlet /> for nested routes.
 */
export function ProtectedRoute({ requireRole, redirectTo }: ProtectedRouteProps) {
  const clerkConfigured = isClerkConfigured();

  // Development mode: bypass auth if Clerk is not configured
  if (!clerkConfigured) {
    return (
      <>
        <DevModeWarning />
        <Outlet />
      </>
    );
  }

  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();

  // Show loading state while Clerk initializes
  if (!isLoaded) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        color: 'var(--color-text-secondary)',
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-sm)',
      }}>
        <span>Initializing...</span>
      </div>
    );
  }

  // Redirect to sign-in if not authenticated
  if (!isSignedIn) {
    const redirectUrl = redirectTo ?? '/sign-in';
    return <Navigate to={redirectUrl} replace />;
  }

  // Check role requirements
  if (requireRole) {
    const userRole = getUserRole(user);
    const roleHierarchy: Record<UserRole, number> = {
      student: 0,
      teacher: 1,
      institution_admin: 2,
      super_admin: 3,
    };

    if (roleHierarchy[userRole] < roleHierarchy[requireRole]) {
      // Redirect to home if user lacks required role
      return <Navigate to="/" replace />;
    }
  }

  // Render child routes
  return <Outlet />;
}

/**
 * Higher-order component version for wrapping individual components
 * Use this when you need to protect a single component instead of routes
 */
export function withProtection<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<ProtectedRouteProps, 'redirectTo'>
) {
  return function ProtectedComponent(props: P) {
    const clerkConfigured = isClerkConfigured();

    // Development mode: bypass auth if Clerk is not configured
    if (!clerkConfigured) {
      return (
        <>
          <DevModeWarning />
          <Component {...props} />
        </>
      );
    }

    const { isLoaded, isSignedIn } = useAuth();
    const { user } = useUser();

    // Show loading state while Clerk initializes
    if (!isLoaded) {
      return (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-mono)',
          fontSize: 'var(--font-size-sm)',
        }}>
          <span>Initializing...</span>
        </div>
      );
    }

    // Redirect to sign-in if not authenticated
    if (!isSignedIn) {
      return <Navigate to="/sign-in" replace />;
    }

    // Check role requirements
    if (options?.requireRole) {
      const userRole = getUserRole(user);
      const roleHierarchy: Record<UserRole, number> = {
        student: 0,
        teacher: 1,
        institution_admin: 2,
        super_admin: 3,
      };

      if (roleHierarchy[userRole] < roleHierarchy[options.requireRole]) {
        return <Navigate to="/" replace />;
      }
    }

    return <Component {...props} />;
  };
}

/**
 * Warning banner displayed when auth is bypassed in dev mode
 */
function DevModeWarning() {
  return (
    <div style={{
      padding: '8px 16px',
      backgroundColor: '#fef3c7',
      borderBottom: '1px solid #f59e0b',
      color: '#92400e',
      fontSize: '12px',
      fontFamily: 'var(--font-family-mono, monospace)',
      textAlign: 'center',
    }}>
      Auth bypassed in dev mode
    </div>
  );
}
